import { Injectable } from '@angular/core';
import { Router, NavigationEnd, Event } from '@angular/router';
import { filter } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class RoutePersistenceService {
  private readonly CURRENT_ROUTE_KEY = 'currentRoute';
  private readonly DEFAULT_ROUTE = 'conversation-insights';

  constructor(private router: Router) {
    // Listen to route changes and save the current route
    this.router.events.pipe(
      filter((event: Event): event is NavigationEnd => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      this.saveCurrentRoute(event.url);
    });
  }

  /**
   * Save the current route to sessionStorage (Insights Dashboard – cleared when tab closes).
   */
  private saveCurrentRoute(url: string): void {
    const routePath = url.startsWith('/') ? url.slice(1) : url;
    if (this.isMainRoute(routePath)) {
      try {
        sessionStorage.setItem(this.CURRENT_ROUTE_KEY, routePath);
      } catch (e) {
        console.warn('Could not save route to sessionStorage', e);
      }
    }
  }

  /**
   * Get the last saved route from sessionStorage.
   */
  getLastRoute(): string {
    try {
      const savedRoute = sessionStorage.getItem(this.CURRENT_ROUTE_KEY);
      return savedRoute || this.DEFAULT_ROUTE;
    } catch (e) {
      return this.DEFAULT_ROUTE;
    }
  }

  /**
   * Check if the route is a main route (not a sub-route with ID)
   */
  private isMainRoute(routePath: string): boolean {
    const mainRoutes = [
      'conversation-insights',
      'taxonomy-dashboard',
      'taxonomy-dashboard/all-taxonomies',
      'taxonomy-dashboard/playground',
      'user-dashboard',
      'create-dashboard',
      'report',
      'settings',
      'nova'
    ];

    // Check if it's a main route or starts with a main route
    return mainRoutes.some(route =>
      routePath === route || routePath.startsWith(route + '/')
    );
  }

  /**
   * Clear the saved route (e.g. on logout).
   */
  clearSavedRoute(): void {
    try {
      sessionStorage.removeItem(this.CURRENT_ROUTE_KEY);
    } catch (e) {
      // ignore
    }
  }

  /**
   * Navigate to the last saved route or default route
   */
  navigateToLastRoute(): void {
    const lastRoute = this.getLastRoute();
    this.router.navigate([lastRoute]);
  }

  /**
   * Check if the current route matches the last saved route
   */
  isCurrentRouteSaved(): boolean {
    const currentRoute = this.router.url.slice(1); // Remove leading slash
    const savedRoute = this.getLastRoute();
    return currentRoute === savedRoute;
  }

  /**
   * Check if the current route is insights dashboard
   */
  isCurrentRouteInsights(): boolean {
    const currentRoute = this.router.url.slice(1); // Remove leading slash
    return currentRoute === 'conversation-insights';
  }

  /**
   * Check if the last saved route was taxonomy dashboard
   */
  wasLastRouteTaxonomy(): boolean {
    const savedRoute = this.getLastRoute();
    return savedRoute === 'taxonomy-dashboard' || savedRoute.startsWith('taxonomy-dashboard/');
  }
}
